import * as React from 'react';

import { IGlob } from '../../ts/Glob';
import { ISizeObj, ISizesObj } from './Interfaces';
import * as gridLayout from './gridLayout';

import { ParameterField } from '../../../../_common/platform/tsx/ParameterField';
import BooleanField from '../../../../_common/platform/tsx/BooleanField';
import NumberField from '../../../../_common/platform/tsx/NumberField';

interface IDialogValue {
    size: number;
    oldSize?: number;
    fixSize: boolean;
    minSize: boolean;
    hide?: boolean;
    allowDocking?: boolean;
    initiallyDocked?: boolean;
    allowResize?: boolean;
}

interface IGridCustomizationDialogOptions {
    value: IDialogValue;
    isLast: boolean;
}

function GridCustomizationDialog(props: {
    setConfirmValueGetter?(getConfirmValue: () => any);
    unsetConfirmValueGetter?();

    options?: IGridCustomizationDialogOptions;
    glob: IGlob;
}) {
    const confirmValueGetter = React.useRef(null);
    const dialogValue = React.useRef({ ...props.options.value });
    const [updateKey, setUpdateKey] = React.useState({});

    React.useEffect(() => {
        if (props.setConfirmValueGetter != null) {
            confirmValueGetter.current = props.setConfirmValueGetter((): IDialogValue => {
                return dialogValue.current;
            });
        }

        return () => {
            if (typeof props.unsetConfirmValueGetter === 'function') {
                props.unsetConfirmValueGetter();
            }
        };
    }, []);

    const currentValue = dialogValue.current;

    const dropHide = () => {
        currentValue.hide = undefined;

        if (currentValue.oldSize != null) {
            currentValue.size = currentValue.oldSize;
            currentValue.oldSize = undefined;
        } else if (currentValue.size === gridLayout.WIDGET_HIDING_SIZE) {
            currentValue.size = 400;
        }
    };

    const handleOnSizeChange = (value: number) => {
        if (value < gridLayout.MIN_SIZE) {
            value = gridLayout.MIN_SIZE;
        }

        currentValue.size = value;

        if (value) {
            currentValue.minSize = false;
            currentValue.allowResize = undefined;
        }

        setUpdateKey({});
    };

    const handleOnFixSizeChange = (value: boolean) => {
        currentValue.fixSize = value;

        if (value) {
            currentValue.minSize = false;
            currentValue.allowResize = undefined;
            dropHide();
        }

        setUpdateKey({});
    };

    const handleOnMinSizeChange = (value: boolean) => {
        currentValue.minSize = value;

        if (value) {
            currentValue.fixSize = false;
            dropHide();
        }

        setUpdateKey({});
    };

    const handleOnHideChange = (value: boolean) => {
        if (value) {
            currentValue.hide = true;
            currentValue.fixSize = false;
            currentValue.minSize = false;
            currentValue.allowDocking = undefined;
            currentValue.initiallyDocked = undefined;
            currentValue.oldSize = currentValue.size;
            currentValue.size = gridLayout.WIDGET_HIDING_SIZE;
        } else {
            dropHide();
        }

        setUpdateKey({});
    };

    const handleOnAllowDockingChange = (value: boolean) => {
        if (value) {
            currentValue.allowDocking = true;
            dropHide();
        } else {
            delete currentValue.allowDocking;
            currentValue.initiallyDocked = undefined;
        }

        setUpdateKey({});
    };

    const handleOnInitiallyDockedChange = (value: boolean) => {
        currentValue.initiallyDocked = value;

        if (value) {
            currentValue.allowDocking = true;
            currentValue.hide = undefined;
        }

        setUpdateKey({});
    };

    const handleOnAllowResizeChange = (value: boolean) => {
        if (value) {
            currentValue.allowResize = true;
            currentValue.fixSize = false;
        } else {
            delete currentValue.allowResize;
        }

        setUpdateKey({});
    };

    const disableSizeChanging = currentValue.hide || !currentValue.fixSize;

    let allowResizeElement: React.JSX.Element;

    if (!props.options.isLast) {
        allowResizeElement = (
            <ParameterField noTitle={true} clearable={false}>
                <BooleanField
                    allowNull={false}
                    value={currentValue.allowResize}
                    title={'Allow User To Resize'}
                    onChange={handleOnAllowResizeChange}
                />
            </ParameterField>
        );
    }

    return (
        <div className="grid-customization-dialog">
            <ParameterField title={'Size'} clearable={false} grayedOut={disableSizeChanging}>
                <NumberField
                    allowNull={false}
                    integer={false}
                    value={currentValue.size}
                    min={0}
                    readOnly={disableSizeChanging}
                    onChange={handleOnSizeChange}
                />
            </ParameterField>

            <ParameterField noTitle={true} clearable={false}>
                <BooleanField
                    allowNull={false}
                    value={currentValue.fixSize}
                    title={'Fix the current size'}
                    onChange={handleOnFixSizeChange}
                />
            </ParameterField>

            <ParameterField noTitle={true} clearable={false}>
                <BooleanField
                    allowNull={false}
                    value={currentValue.minSize}
                    title={'Make the current size be minimal'}
                    onChange={handleOnMinSizeChange}
                />
            </ParameterField>

            <ParameterField noTitle={true} clearable={false}>
                <BooleanField
                    allowNull={false}
                    value={currentValue.hide}
                    title={'Hide'}
                    onChange={handleOnHideChange}
                />
            </ParameterField>

            <ParameterField noTitle={true} clearable={false}>
                <BooleanField
                    allowNull={false}
                    value={currentValue.allowDocking}
                    title={'Allow Docking'}
                    onChange={handleOnAllowDockingChange}
                />
            </ParameterField>

            <ParameterField noTitle={true} clearable={false}>
                <BooleanField
                    allowNull={false}
                    value={currentValue.initiallyDocked}
                    title={'Initially Docked'}
                    onChange={handleOnInitiallyDockedChange}
                />
            </ParameterField>

            {allowResizeElement}
        </div>
    );
}

function applyChanges(value: IDialogValue, obj: ISizeObj, sizesObj: ISizesObj) {
    const newSize = value.size;
    const newInitiallyDocked = value.initiallyDocked;

    let refresh = false;
    let toggleDockedState = false;

    if (newSize !== obj.size) {
        obj.origin.size = newSize;
    }

    if (value.fixSize !== obj.origin.fixed) {
        if (value.fixSize) {
            obj.origin.fixed = true;
            obj.origin.size = newSize;
        } else {
            delete obj.origin.fixed;
            delete obj.origin.size;
        }

        gridLayout.setLayoutFromSizes(sizesObj.arr, false);
        refresh = true;
    }

    if (value.minSize !== (obj.origin.min != null)) {
        if (value.minSize) {
            obj.origin.min = newSize;
        } else {
            delete obj.origin.min;
        }

        gridLayout.setLayoutFromSizes(sizesObj.arr, false);
        refresh = true;
    }

    if (value.allowResize !== obj.origin.allowResize) {
        if (value.allowResize) {
            obj.origin.allowResize = true;
        } else {
            delete obj.origin.allowResize;
        }
    }

    if (value.allowDocking !== obj.origin.allowDocking) {
        if (value.allowDocking) {
            obj.origin.allowDocking = true;
        } else {
            delete obj.origin.allowDocking;

            if (obj.origin.docked) {
                toggleDockedState = true;
            }
        }
    }

    if (newInitiallyDocked !== obj.origin.initiallyDocked) {
        if (newInitiallyDocked) {
            obj.origin.initiallyDocked = true;

            if (!obj.origin.docked) {
                toggleDockedState = true;
            }
        } else {
            delete obj.origin.initiallyDocked;

            if (obj.origin.docked) {
                toggleDockedState = true;
            }
        }
    } else if (obj.origin.docked !== newInitiallyDocked) {
        toggleDockedState = true;
    }

    if (value.hide !== obj.origin.hide) {
        if (value.hide) {
            obj.origin.hide = true;
            obj.size = obj.origin.size = gridLayout.WIDGET_HIDING_SIZE;
            toggleDockedState = false;
        } else {
            delete obj.origin.hide;
            obj.size = 400;
            delete obj.origin.size;
        }

        gridLayout.setLayoutFromSizes(sizesObj.arr, false);
    }

    if (toggleDockedState) {
        gridLayout.toggleDockedState(obj);
        refresh = true;
    }

    return refresh;
}

export function showGridCustomizationDialog(
    obj: ISizeObj,
    sizesObj: ISizesObj,
    elementType: gridLayout.ElementType,
    isLast: boolean,
    glob: IGlob,
    onChange: (refresh: boolean) => void
) {
    glob.DialogActions.createDialogCustom<IGridCustomizationDialogOptions, any, any>(
        elementType === 'column' ? 'Column Settings' : 'Row Settings',
        (value: IDialogValue) => {
            const refresh = applyChanges(value, obj, sizesObj);

            onChange(refresh);
        },
        () => {},
        () => {},
        {
            deny: false,
            confirmTitle: 'OK',
            defaultHeight: '1/4',
            defaultWidth: '1/4',
            componentOptions: {
                value: {
                    size: obj.size,
                    fixSize: obj.origin.fixed,
                    minSize: obj.origin.min != null,
                    hide: obj.origin.hide,
                    allowDocking: obj.origin.allowDocking,
                    initiallyDocked: obj.origin.initiallyDocked,
                    allowResize: obj.origin.allowResize
                },
                isLast: isLast
            },

            Component: GridCustomizationDialog
        }
    );
}
